import { useState } from "react";
import {
  PageShell,
  PageHeader,
  ListView,
  ListSkeleton,
  EmptyState,
} from "../../../Components/shared";
import { Pagination } from "../../../Components/atoms";
import { FaCoffee, FaTools, FaVial } from "react-icons/fa";
import useAuth from "../../../Hooks/useAuth";
import {
  useGetFactoryMachinesQuery,
  useGetFactoryBuildsQuery,
} from "../../../store";

const TABLE_HEADERS = [
  { label: "MAKİNE SERİ NO", className: "w-[20%]" },
  { label: "TİP", className: "w-[24%]" },
  { label: "KÖKEN", className: "w-[10%]" },
  { label: "PARÇA", className: "w-[8%]" },
  { label: "MONTE EDEN", className: "w-[16%]" },
  { label: "BÖLGE", className: "w-[10%]" },
  { label: "TARİH", className: "w-[12%]" },
];

const formatDate = (iso) => {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** Fabrika Makineler — devam eden montaj/bakımlar + tamamlanmış üretim kayıtları. */
export default function FactoryMachinesPage() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const [page, setPage] = useState(1);

  const { data: builds = [], isLoading: buildsLoading } = useGetFactoryBuildsQuery(
    { token },
    { pollingInterval: 30_000, skip: !token }
  );

  const {
    data: machinesData,
    isLoading: machinesLoading,
    isFetching: machinesFetching,
  } = useGetFactoryMachinesQuery({ token, page, limit: 25 }, { skip: !token });

  const loading = machinesLoading || machinesFetching;
  const machines = machinesData?.items || [];
  const totalCount = machinesData?.totalCount || 0;
  const totalPages = Math.max(Math.ceil(totalCount / 25), 1);

  return (
    <PageShell>
      <div className="px-3 py-2.5 sm:p-4 md:p-6 border-b border-gray-100 shrink-0">
        <PageHeader
          icon={FaCoffee}
          iconColor="indigo"
          title="Fabrika Makineleri"
          subtitle="Üretim kayıtları ve devam eden montaj / bakım"
          stats={[
            { label: "Üretilen", value: totalCount },
            { label: "Devam Eden", value: builds.length },
          ]}
          isLoading={buildsLoading || machinesLoading}
        />

        {/* Devam eden montaj / bakım kartları */}
        {builds.length > 0 && (
          <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
            {builds.map((b) => (
              <div
                key={b._id}
                className="shrink-0 bg-white border border-gray-200 rounded-xl px-3 py-2 min-w-[200px]"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-gray-900 truncate">
                    {b.machineType?.name || "-"}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                      b.mode === "bakim"
                        ? "bg-amber-50 text-amber-700 border-amber-100"
                        : b.status === "testing"
                          ? "bg-purple-50 text-purple-700 border-purple-100"
                          : "bg-indigo-50 text-indigo-700 border-indigo-100"
                    }`}
                  >
                    {b.mode === "bakim" ? (
                      <>
                        <FaTools className="w-2.5 h-2.5" /> Bakımda
                      </>
                    ) : b.status === "testing" ? (
                      <>
                        <FaVial className="w-2.5 h-2.5" /> Testte
                      </>
                    ) : (
                      "Montajda"
                    )}
                  </span>
                </div>
                <div className="mt-1 text-[11px] text-gray-500">
                  {b.attached?.length || 0} parça takılı
                  {b.device?.serialNo && (
                    <span className="ml-1 font-mono">· {b.device.serialNo}</span>
                  )}
                </div>
                <div className="text-[10px] text-gray-400">
                  {b.startedBy?.name || "-"} · {formatDate(b.startedAt)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ListView
        headers={TABLE_HEADERS}
        tableClassName="table-fixed"
        data={machines}
        isLoading={loading}
        loadingNode={
          <ListSkeleton
            columns={TABLE_HEADERS.map((h) => ({ label: h.label }))}
            rowCount={15}
            message="Üretim kayıtları yükleniyor..."
          />
        }
        empty={
          <EmptyState
            message="Henüz üretim kaydı yok"
            suggestion="Montaj tamamlandıkça makineler burada listelenecek"
          />
        }
        footer={
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalCount}
            itemsPerPage={25}
            onPageChange={setPage}
            disabled={loading}
          />
        }
        renderRow={(m) => (
          <tr key={m._id} className="border-b border-gray-100 hover:bg-gray-50">
            <td className="px-4 py-3 font-mono text-sm font-semibold text-gray-900 whitespace-nowrap">
              {m.device?.serialNo || "-"}
            </td>
            <td className="px-4 py-3 text-sm text-gray-700 truncate">
              {m.machineType?.name || "-"}
            </td>
            <td className="px-4 py-3">
              <span
                className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${
                  m.origin === "imported"
                    ? "bg-gray-50 text-gray-600 border-gray-100"
                    : "bg-emerald-50 text-emerald-700 border-emerald-100"
                }`}
              >
                {m.origin === "imported" ? "İthal" : "Üretim"}
              </span>
            </td>
            <td className="px-4 py-3 text-sm text-gray-700">
              {m.partUnits?.length || 0}
            </td>
            <td className="px-4 py-3 text-sm text-gray-500">
              {m.assembledBy?.name || "-"}
            </td>
            <td className="px-4 py-3 text-sm text-gray-500">
              {m.zone?.name || "-"}
            </td>
            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
              {formatDate(m.assembledAt)}
            </td>
          </tr>
        )}
        renderMobileCard={(m) => (
          <div
            key={m._id}
            className="bg-white border border-gray-200 rounded-xl p-3 flex flex-col gap-1"
          >
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm font-semibold text-gray-900">
                {m.device?.serialNo || "-"}
              </span>
              <span className="text-[11px] text-gray-500">
                {m.partUnits?.length || 0} parça
              </span>
            </div>
            <div className="text-xs text-gray-600">{m.machineType?.name}</div>
            <div className="text-[11px] text-gray-400">
              {m.assembledBy?.name || "-"} · {formatDate(m.assembledAt)}
            </div>
          </div>
        )}
      />
    </PageShell>
  );
}
